/**
 * Escritas dos alertas e das notificações (Alertas.dc.html e o sino): resolver,
 * adiar e desfazer um alerta; marcar notificações como lidas. A tela muda na
 * hora e volta atrás se o servidor recusar.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { chavesDosAlertas, lerDados } from './dadosCasca';

const POST = (corpo) => ({ method: 'POST', body: JSON.stringify(corpo || {}) });

export function useEscritasDosAlertas() {
  const queryClient = useQueryClient();

  const trocar = async (chave, mudar) => {
    await queryClient.cancelQueries({ queryKey: chave });
    const antes = queryClient.getQueryData(chave);
    if (antes) queryClient.setQueryData(chave, mudar(antes));
    return { antes };
  };
  const voltar = (chave) => (_erro, _vars, ctx) => { if (ctx?.antes) queryClient.setQueryData(chave, ctx.antes); };

  const dispensar = useMutation({
    mutationFn: ({ chave, acao }) => lerDados('/api/alertas/dispensas', POST({ chave, acao })),
    onMutate: ({ chave }) => trocar(chavesDosAlertas.alertas, (d) => ({ ...d, alertas: (d.alertas || []).filter((a) => a.chave !== chave) })),
    onError: voltar(chavesDosAlertas.alertas),
  });
  const desfazer = useMutation({
    mutationFn: (chave) => lerDados('/api/alertas/dispensas/desfazer', POST({ chave })),
    onSettled: () => queryClient.invalidateQueries({ queryKey: chavesDosAlertas.alertas }),
  });

  const marcarComoLidas = (ids) => (d) => {
    const alvo = ids ? new Set(ids) : null;
    let marcadas = 0;
    const itens = (d.itens || []).map((n) => {
      if (n.lida || (alvo && !alvo.has(n.id))) return n;
      marcadas += 1;
      return { ...n, lida: true };
    });
    return { ...d, itens, nao_lidas: alvo ? Math.max(0, (d.nao_lidas || 0) - marcadas) : 0 };
  };
  const aoTerminarLeitura = () => {
    queryClient.invalidateQueries({ queryKey: chavesDosAlertas.notificacoes });
    queryClient.invalidateQueries({ queryKey: ['radar', 'avisos'] });
  };
  const lidas = useMutation({
    mutationFn: (ids) => lerDados('/api/notificacoes/lidas', POST({ ids })),
    onMutate: (ids) => trocar(chavesDosAlertas.notificacoes, marcarComoLidas(ids)),
    onError: voltar(chavesDosAlertas.notificacoes),
    onSettled: aoTerminarLeitura,
  });
  const todas = useMutation({
    mutationFn: () => lerDados('/api/notificacoes/lidas/todas', POST()),
    onMutate: () => trocar(chavesDosAlertas.notificacoes, marcarComoLidas(null)),
    onError: voltar(chavesDosAlertas.notificacoes),
    onSettled: aoTerminarLeitura,
  });

  return {
    dispensar: dispensar.mutateAsync,
    desfazer: desfazer.mutateAsync,
    marcarLidas: lidas.mutateAsync,
    marcarTodas: todas.mutateAsync,
    marcandoTodas: todas.isPending,
  };
}
